<script setup>
const features = [
	{
		name: 'Layout and Design',
		description: 'White space, alignment/ spacing of elements, symmetry, use of color, consistency of margins, use of visual hierarchy, focal point definition, etc.',
		svg: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke-width="1.5" color="currentColor" viewBox="0 0 24 24"><path stroke="currentColor" d="M20.4 3H3.6a.6.6 0 0 0-.6.6v16.8a.6.6 0 0 0 .6.6h16.8a.6.6 0 0 0 .6-.6V3.6a.6.6 0 0 0-.6-.6ZM14.25 9.75V21M21 9.75H3"/></svg>`,
	},
	{
		name: 'Content and Narrative',
		description: 'Clarity of message, grammar/spelling, consistency of voice, redundancy, "rule of three", concrete vs abstract language, storyboarding, etc.',
		svg: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke-width="1.5" color="currentColor" viewBox="0 0 24 24"><path stroke="currentColor" stroke-linecap="round" d="M12 21V7a2 2 0 0 1 2-2h7.4a.6.6 0 0 1 .6.6v13.114M12 21V7a2 2 0 0 0-2-2H2.6a.6.6 0 0 0-.6.6v13.114M14 19h8M10 19H2"/><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" d="M12 21a2 2 0 0 1 2-2M12 21a2 2 0 0 0-2-2"/></svg>`,
	},
	{
		name: 'Typography',
		description: 'Font hierarchy, font size/ weight, spacing, legibility, reader interpretation, text alignment, use of bold/ italics, indents, consistency, etc.',
		svg: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke-width="1.5" color="currentColor" viewBox="0 0 24 24"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" d="M12 8v8m0-8H8m4 0h4"/><path stroke="currentColor" stroke-linejoin="round" d="M21 13.5V19a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-5.5m18-3V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v5.5M19.5 13.5v-3h3v3h-3ZM1.5 13.5v-3h3v3h-3Z"/></svg>`,
	},
	{
		name: "Visual Elements",
		description: "Use of icons/ shapes, photos, diagrams, use of color, labeling, logical progression/ interpretability, alignment with text/ narrative, etc.",
		svg: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke-width="1.5" color="currentColor" viewBox="0 0 24 24"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" d="M21 7.353v9.294a.6.6 0 0 1-.309.525l-8.4 4.666a.6.6 0 0 1-.582 0l-8.4-4.666A.6.6 0 0 1 3 16.647V7.353a.6.6 0 0 1 .309-.524l8.4-4.667a.6.6 0 0 1 .582 0l8.4 4.667a.6.6 0 0 1 .309.524Z"/><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" d="m3.528 7.294 8.18 4.544a.6.6 0 0 0 .583 0l8.209-4.56M12 21v-9"/></svg>`
	},
	{
		name: "Data Visualization",
		description: "Chart type selection, written/ implied context, interpretability, scale/ proportion of data, use of color, annotations, legend/ key readability, etc.",
		svg: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke-width="1.5" color="currentColor" viewBox="0 0 24 24"><path stroke="currentColor" d="M9 21h6m-6 0v-5m0 5H3.6a.6.6 0 0 1-.6-.6v-3.8a.6.6 0 0 1 .6-.6H9m6 5V9m0 12h5.4a.6.6 0 0 0 .6-.6V3.6a.6.6 0 0 0-.6-.6h-4.8a.6.6 0 0 0-.6.6V9m0 0H9.6a.6.6 0 0 0-.6.6V16"/></svg>`
	},
	{
		name: 'Engagement and Interactivity',
		description: 'Audience focus, presentation venue, perceived audience context, call-to-action clarity, use of prompts, emotional appeal, engagement cues, etc.',
		svg: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke-width="1.5" color="currentColor" viewBox="0 0 24 24"><path stroke="currentColor" stroke-linecap="round" d="M1 20v-1a7 7 0 0 1 7-7v0a7 7 0 0 1 7 7v1"/><path stroke="currentColor" stroke-linecap="round" d="M13 14v0a5 5 0 0 1 5-5v0a5 5 0 0 1 5 5v.5"/><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" d="M8 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM18 9a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z"/></svg>`
	}
]
</script>

<template>
	<div class="mx-auto mt-4 max-w-7xl px-6 sm:mt-8 md:mt-12 lg:px-8">
		<dl class="mx-auto grid max-w-2xl grid-cols-1 gap-6 text-gray-600 sm:grid-cols-2 lg:mx-0 lg:max-w-none lg:grid-cols-3 lg:gap-8">
			<div
				v-for="feature in features"
				:key="feature.name"
				class="group relative h-full transition-all duration-200 hover:-translate-y-1"
			>
				<!-- Main card that expands on mobile -->
				<div class="relative flex flex-col h-full overflow-hidden rounded-xl border border-gray-200 bg-white p-6 md:p-8 lg:p-6 xl:p-8 shadow-sm transition-all duration-300 group-hover:shadow-md group-hover:border-gray-300">

					<!-- Icon and title (always visible) -->
					<dt class="font-semibold text-gray-900">
						<div class="mb-4 flex h-10 w-10 items-center justify-center rounded-lg bg-gray-50 border border-gray-200 transition-colors duration-200 group-hover:bg-gray-100">
							<div v-html="feature.svg" class="h-5 w-5 text-gray-600 group-hover:text-gray-700" aria-hidden="true"></div>
						</div>
						<span class="text-lg font-semibold text-gray-900 tracking-tight">
							{{ feature.name }}
						</span>
					</dt>

					<!-- Expandable description container - collapses on mobile, always visible on sm+ -->
					<dd class="text-gray-600 leading-relaxed text-sm overflow-hidden transition-all duration-300 ease-out max-h-0 group-hover:max-h-32 group-hover:mt-4 sm:max-h-none sm:mt-4 sm:flex-1">
						<div class="transform transition-all duration-300 ease-out opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 sm:opacity-100 sm:translate-y-0">
							{{ feature.description }}
						</div>
					</dd>

					<!-- Subtle accent line - hidden on mobile until hover, always visible on sm+ -->
					<div class="h-px bg-gradient-to-r from-gray-200 to-transparent opacity-0 transition-all duration-300 mt-0 group-hover:opacity-100 group-hover:mt-4 sm:opacity-100 sm:mt-4"></div>

					<!-- Mobile-only hover indicator -->
					<div class="absolute bottom-4 right-4 opacity-50 transition-opacity duration-200 group-hover:opacity-0 sm:hidden">
						<svg class="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
							<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
						</svg>
					</div>
				</div>
			</div>
		</dl>
	</div>
</template>

<style scoped>

</style>